//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@carbon/icons-react';
import TenantDataService from '../../../utils/TenantDataService';
import LoopDataService from '../../../utils/LoopDataService';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import MemberList from './MemberList';
import UpdateMembersProgress from './UpdateMembersProgress';
import {
  FetchKeycloakUserResponse,
  FetchTeamspaceUserResponse,
} from '../../Sidebar/SidebarTypes';
import { useUserAuth } from '../../../UserAuth';

interface UserInfo {
  username: string;
  fullname: string;
  email?: string;
}

interface EditModalProps {
  isVisible: boolean;
  onClose: () => void;
  loopName: string | undefined;
  loopId: string | undefined;
  associatedUsers: UserInfo[];
  showSnackbar?: (message: string, type?: string) => void;
  refreshLoops?: () => void;
}

/**
 * A modal for editing the members of a loop or teamspace.
 *
 * @param {object} props The component props.
 * @param {boolean} props.isVisible Controls modal visibility.
 * @param {function} props.onClose Callback to close the modal.
 * @param {string} [props.loopName] The name of the loop or teamspace.
 * @param {string} [props.loopId] The ID of the loop.
 * @param {UserInfo[]} props.associatedUsers The initial list of members.
 * @param {function} [props.refreshLoops] Callback to refresh data after saving.
 * @param {function} [props.showSnackbar] Callback to show notifications.
 * @returns {JSX.Element | null} The rendered modal or null if not visible.
 */
const EditModal: React.FC<EditModalProps> = (props) => {
  // When isVisible is false, EditModalContent unmounts.
  // When it becomes true again, it remounts with fresh state.
  if (!props.isVisible) {
    return null;
  }

  return <EditModalContent {...props} />;
};

const EditModalContent: React.FC<EditModalProps> = ({
  isVisible,
  onClose,
  loopName,
  loopId,
  associatedUsers,
  refreshLoops,
  showSnackbar,
}) => {
  const { t } = useTranslation();
  const { teamspaceId } = useTeamspaceContext();
  const { user, isAuthenticated } = useUserAuth();
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );
  const [selectedKnownUsers, setSelectedKnownUsers] =
    useState<UserInfo[]>(associatedUsers);
  const [newlyInvitedEmails, setNewlyInvitedEmails] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<UserInfo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProgressView, setShowProgressView] = useState(false);
  const [newlyAddedUsernames, setNewlyAddedUsernames] = useState<Set<string>>(
    new Set(),
  );
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);
  const mode = refreshLoops ? 'loop' : 'teamspace';
  const [userLoopMemberships, setUserLoopMemberships] = useState<
    Map<string, string[]>
  >(new Map());

  useEffect(() => {
    const fetchUserLoopMemberships = async () => {
      if (isVisible && mode === 'teamspace' && teamspaceId) {
        try {
          const response = await loopDataService.getLoop(teamspaceId);
          const memberships = new Map<string, string[]>();
          if (response?._embedded?.loops) {
            for (const loop of response._embedded.loops) {
              for (const userId of loop.associatedUserIds) {
                const userMemberships = memberships.get(userId) || [];
                userMemberships.push(loop.displayName);
                memberships.set(userId, userMemberships);
              }
            }
          }
          setUserLoopMemberships(memberships);
        } catch (error) {
          setUserLoopMemberships(new Map());
        }
      }
    };
    fetchUserLoopMemberships();
  }, [isVisible, mode, teamspaceId, loopDataService]);

  const hasChanges = useMemo(() => {
    const usersActuallyAdded = selectedKnownUsers.filter(
      (user) => !associatedUsers.some((au) => au.username === user.username),
    );
    const usersActuallyRemoved = associatedUsers.filter(
      (user) => !selectedKnownUsers.some((su) => su.username === user.username),
    );
    return (
      usersActuallyAdded.length > 0 ||
      usersActuallyRemoved.length > 0 ||
      newlyInvitedEmails.length > 0
    );
  }, [selectedKnownUsers, newlyInvitedEmails, associatedUsers]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        let fetchedUsers: UserInfo[] = [];
        if (refreshLoops) {
          const users: FetchTeamspaceUserResponse =
            await tenantDataService.fetchTeamspaceMembers(teamspaceId || '');
          fetchedUsers = users.map((user) => ({
            username: user.username,
            fullname: user.fullname || user.username,
            email: (user as any).email,
          }));
        } else {
          const users: FetchKeycloakUserResponse =
            await tenantDataService.fetchKeycloakUsers();
          fetchedUsers = users.map((user) => ({
            username: user.username,
            fullname: user.fullname || user.username,
            email: (user as any).email,
          }));
        }

        const userMap = new Map<string, UserInfo>();

        associatedUsers.forEach((user) =>
          userMap.set(user.username.toLowerCase(), user),
        );

        fetchedUsers.forEach((user) => {
          if (!userMap.has(user.username.toLowerCase())) {
            userMap.set(user.username.toLowerCase(), user);
          }
        });

        setAllUsers(Array.from(userMap.values()));

        setHasFetchedUsers(true);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    if (isVisible && !hasFetchedUsers) {
      fetchAllUsers();
    }
  }, [
    isVisible,
    teamspaceId,
    refreshLoops,
    associatedUsers,
    hasFetchedUsers,
    tenantDataService,
  ]);
  const handleRemoveUser = useCallback(
    (username: string) => {
      const isInvited = newlyInvitedEmails.some(
        (email) => email.toLowerCase() === username.toLowerCase(),
      );

      if (isInvited) {
        setNewlyInvitedEmails((prev) =>
          prev.filter(
            (email) => email.toLowerCase() !== username.toLowerCase(),
          ),
        );
      } else {
        setSelectedKnownUsers((prev) =>
          prev.filter(
            (user) => user.username.toLowerCase() !== username.toLowerCase(),
          ),
        );
      }

      setNewlyAddedUsernames((prev) => {
        if (!prev.has(username)) return prev;
        const next = new Set(prev);
        next.delete(username);
        return next;
      });
    },
    [newlyInvitedEmails],
  );

  const usersToAdd = useMemo(
    () =>
      allUsers.filter(
        (user) =>
          !selectedKnownUsers.some((u) => u.username === user.username) &&
          !newlyInvitedEmails.some(
            (email) => email === user.email || email === user.username,
          ) &&
          (user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.fullname.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [allUsers, selectedKnownUsers, newlyInvitedEmails, searchQuery],
  );

  const handleSelectUser = (userToAdd: UserInfo) => {
    const isAlreadySelected = selectedKnownUsers.some(
      (user) =>
        user.username.toLowerCase() === userToAdd.username.toLowerCase(),
    );
    if (!isAlreadySelected) {
      setSelectedKnownUsers((prevUsers) => [...prevUsers, userToAdd]);
      setNewlyAddedUsernames((prev) => new Set(prev).add(userToAdd.username));
    }
  };

  const handleInviteEmail = (emailToAdd: string) => {
    const lowercasedEmail = emailToAdd.toLowerCase();
    const existsAsKnownUser = selectedKnownUsers.some(
      (user) => user.username.toLowerCase() === lowercasedEmail,
    );
    const existsAsInvitedEmail = newlyInvitedEmails.some(
      (email) => email.toLowerCase() === lowercasedEmail,
    );
    if (!existsAsKnownUser && !existsAsInvitedEmail) {
      setNewlyInvitedEmails((prevEmails) => [...prevEmails, emailToAdd]);
      setNewlyAddedUsernames((prev) => new Set(prev).add(emailToAdd));
    }
  };

  const invitedUsersAsUserInfo = newlyInvitedEmails.map((email) => ({
    username: email,
    fullname: email,
    email: email,
  }));
  const combinedDisplayList = [
    ...selectedKnownUsers,
    ...invitedUsersAsUserInfo,
  ];

  const handleCloseAndRefresh = useCallback(async () => {
    if (refreshLoops) {
      await refreshLoops();
    }
    onClose();
  }, [onClose, refreshLoops]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        handleCloseAndRefresh();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isVisible, handleCloseAndRefresh]);

  return (
    <div data-testid="edit-modal-wrapper">
      {/* Backdrop */}
      <div
        id="edit-modal-overlay"
        data-testid="edit-modal-overlay"
        role="button"
        tabIndex={-1}
        className="fixed inset-0 top-12 z-[1150] bg-black/20"
        onClick={handleCloseAndRefresh}
        onKeyDown={(e) => {
          if (e.key === 'Escape') handleCloseAndRefresh();
        }}
      />

      {/* Sidebar */}
      <div
        id="edit-modal"
        data-testid="edit-modal-sidebar"
        className="fixed bottom-0 right-0 top-12 z-[1151] flex w-[657px] flex-col bg-card shadow-xl transition-colors"
      >
        {/* Header */}
        <div
          id="edit-modal-close-container"
          data-testid="edit-modal-header"
          className="flex items-center justify-between border-b border-gray-200 bg-secondary px-4 py-3 dark:border-gray-700 dark:bg-carbon-darkPanel"
        >
          <h2 className="text-[16px] font-semibold text-gray-700 dark:text-gray-300">
            {t('edit-members', 'Edit Members')}
          </h2>
          <button
            onClick={handleCloseAndRefresh}
            className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            <Close
              size={16}
              className="cursor-pointer text-gray-600 dark:text-gray-400"
              aria-label={t('Close')}
            />
          </button>
        </div>

        {showProgressView ? (
          <div
            id="id-edit-modal-progress-wrapper"
            data-testid="edit-modal-progress-wrapper"
            className="flex-1 overflow-y-auto p-5"
          >
            <UpdateMembersProgress
              loopName={loopName}
              loopId={loopId}
              teamspaceId={teamspaceId || ''}
              originalUsers={associatedUsers.map((user) => user.username)}
              showSnackbar={showSnackbar}
              mode={mode}
              finalUserList={selectedKnownUsers.map((u) => u.username)}
              emailsToInvite={newlyInvitedEmails}
              onComplete={refreshLoops}
            />
          </div>
        ) : (
          <>
            <div
              id="id-edit-modal-content-wrapper"
              data-testid="edit-modal-content-wrapper"
              className="flex flex-1 flex-col overflow-y-auto"
            >
              <div
                id="id-edit-modal-member-list-wrapper"
                data-testid="edit-modal-member-list-wrapper"
                className="flex-1 px-4 pt-4"
              >
                {hasFetchedUsers && (
                  <MemberList
                    loopName={loopName}
                    searchQuery={searchQuery}
                    handleRemoveUser={handleRemoveUser}
                    setSearchQuery={setSearchQuery}
                    mode={mode}
                    selectedUsers={combinedDisplayList}
                    userLoopMemberships={userLoopMemberships}
                    allUsers={allUsers}
                    usersToAdd={usersToAdd}
                    onSelectUser={handleSelectUser}
                    onInviteEmail={handleInviteEmail}
                    newlyAddedUsernames={newlyAddedUsernames}
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div
              id="edit-modal-footer"
              data-testid="edit-modal-footer"
              className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-carbon-darkPanel"
            >
              <button
                id="edit-modal-cancel-button"
                onClick={handleCloseAndRefresh}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
              >
                {t('Cancel')}
              </button>
              <button
                id="edit-modal-save-button"
                onClick={() => setShowProgressView(true)}
                disabled={!hasChanges}
                className={`rounded-md px-4 py-2 text-sm font-medium text-primary-foreground transition-colors ${
                  hasChanges
                    ? 'cursor-pointer bg-primary hover:bg-primary-hover'
                    : 'cursor-not-allowed bg-primary opacity-50'
                }`}
              >
                {t('update-members', 'Update Members')}
                {newlyAddedUsernames.size > 0 &&
                  ` (${newlyAddedUsernames.size})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditModal;
